import IconNameEnum from "constants/IconNameEnum";
import React from "react";
import { Col, Form, Row } from "react-bootstrap";
import { connect, DispatchProp } from "react-redux";
import NotaEstimateModel from "stores/note/models/NotaEstimateModel";
import NotaOrderModel from "stores/note/models/NotaOrderModel";
import NuovoNotaFormModel from "stores/note/models/NuovoNotaFormModel";
import NotaAction from "stores/note/NotaAction";
import { IAddNoteRequest, IGetNoteTopicsRequest } from "stores/note/NotaEffect";
import CercaDettalgioPreventivoModel from "stores/ricerca/models/CercaDettalgioPreventivoModel";
import FormatUtility from "utilities/FormatUtility";
import ValidationUtility, { ValidationContainer } from "utilities/ValidationUtility";
import IAction from "../../../models/IAction";
import IStore from "../../../models/IStore";
import NoteTopicsModel from "../../../stores/note/models/NoteTopicsModel";
import BaseModal from "../base-content/BaseModal";
import BaseButton from "../base-form/BaseButton";
import BaseCheckbox from "../base-form/BaseCheckbox";
import BaseInput from "../base-form/BaseInput";
import BaseTextArea from "../base-form/BaseTextArea";
import NoteMessage from "./NoteMessage";
import LoadingIndicator from "../loading-indicator/LoadingIndicator";

interface IProps {
    readonly open: boolean;
    readonly onClose: () => void;
    readonly isRequesting?: boolean;
}

interface IState {
    readonly validationContainer: ValidationContainer<NuovoNotaFormModel>;
}

interface IStateToProps {
    readonly formModel: NuovoNotaFormModel;
    readonly notaInterna: boolean;
    readonly dettaglioPreventivo: CercaDettalgioPreventivoModel;
    readonly noteTopics: NoteTopicsModel;
    readonly estimateNote: NotaEstimateModel[];
    readonly requestNoteModel: NotaOrderModel;
    readonly notaDetails: NotaEstimateModel;
}

const mapStateToProps = (state: IStore): IStateToProps => ({
    formModel: state.nota.nuovoNotaForm,
    notaInterna: state.nota.notaInterna,
    dettaglioPreventivo: state.cerca.dettalgioPreventivoModel!,
    noteTopics: state.nota.noteTopics,
    estimateNote: state.nota.estimateNote,
    requestNoteModel: state.nota.noteTopicsForm,
    notaDetails: state.nota.notaDetails,
});

class NotePopup extends React.Component<IProps & IStateToProps & DispatchProp<IAction<any>>, IState> {
    constructor(props: IProps & IStateToProps & DispatchProp<IAction<any>>, state: IState) {
        super(props);
        this.state = {
            validationContainer: ValidationUtility.setValidationContainer<NuovoNotaFormModel>(
                new NuovoNotaFormModel({})
            ),
        } as IState;
    }

    private hasUnmounted = false;
    private validationSchema = ValidationUtility.getSchemaNote();
    private formRef: React.RefObject<HTMLFormElement> = React.createRef<HTMLFormElement>();

    public componentWillUnmount() {
        this.hasUnmounted = true;
        this.props.dispatch(NotaAction.resetNuovoNota());
    }

    private onChangeHandler = (name: string, value: string | number | boolean | Date) => {
        let formValues = new NuovoNotaFormModel(this.props.formModel);
        formValues = FormatUtility.updateNestedPropertyValue<NuovoNotaFormModel>(formValues, name, value);

        this.props.dispatch(NotaAction.setNuovoNotaForm(formValues));
        this.props.dispatch(NotaAction.setNotaInterna(formValues.notaInterna));
    };

    private onBlurHandler = async (name: string) => {
        let validationContainer = await ValidationUtility.validateSchema<NuovoNotaFormModel>(
            this.validationSchema,
            this.state.validationContainer,
            this.props.formModel,
            name,
            undefined,
            {
                notaInterna: this.props.notaInterna,
            }
        );

        if (this.hasUnmounted === false) {
            this.setState({
                validationContainer: validationContainer,
            });
        }
    };

    private onSubmitHandler = async (
        event: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLElement, MouseEvent>
    ) => {
        event.preventDefault();

        let validationContainer = await ValidationUtility.validateSchema<NuovoNotaFormModel>(
            this.validationSchema,
            this.state.validationContainer,
            this.props.formModel,
            undefined,
            undefined,
            {
                notaInterna: this.props.notaInterna,
            }
        );

        if (this.hasUnmounted === false) {
            this.setState({
                validationContainer: validationContainer,
            });
        }

        if (ValidationUtility.checkValidity(validationContainer) === true) {
            this.submit();
            this.props.onClose();
        } else {
            if (this.hasUnmounted === false) {
                this.setState({}, () => {
                    ValidationUtility.scrollToFirstError<NuovoNotaFormModel>(validationContainer, "formNuovaNota.");
                });
            }
        }
    };

    private submit = async () => {
        let data = new NuovoNotaFormModel({ ...this.props.formModel });
        let { dettaglioPreventivo, notaDetails } = this.props;

        let splittedMail = data.followers.split(";");

        const request: IAddNoteRequest = {
            idEstimate: Number(dettaglioPreventivo?.idEstimate) || notaDetails.idEstimate,
            text: data.nota,
            noteType: data?.notaInterna === true ? 1 : 0,
            followers: splittedMail,
        };

        const requestNoteTopics: IGetNoteTopicsRequest = {
            pageNumber: 0,
            pageSize: 20,
            sortParam: this.props.requestNoteModel.colName!,
            sortAsc: this.props.requestNoteModel.direction === "A" ? true : false,
        };

        await this.props.dispatch(NotaAction.addNota(request));
    };

    private getNoteBackgroundColor = (note: NotaEstimateModel) => {
        const Color = {
            0: "blue",
            1: "dark-blue",
            2: "light-blue",
            default: "yellow",
        };

        return note.isRead ? Color[note.noteType.key] : Color.default;
    };

    private getModalBody = () => {
        const { formModel, notaInterna, estimateNote } = this.props;
        const { validationContainer } = this.state;

        return (
            <>
                <div className="p-3">
                    {estimateNote.map((note: NotaEstimateModel) => (
                        <NoteMessage
                            noteType={note.noteType}
                            userName={note.userName}
                            userSurname={note.userSurname}
                            insertDate={note.insertDate}
                            description={note.description}
                            background={this.getNoteBackgroundColor(note)}
                        />
                    ))}
                </div>
                <Row className="font-weight-bold mt-5">
                    <Col className="center">NUOVA NOTA</Col>
                </Row>
                <Form className="mt-3" ref={this.formRef} noValidate onSubmit={this.onSubmitHandler}>
                    <Row className="pt-2">
                        <Col sm={12}>
                            <BaseTextArea
                                controlId="formNuovaNota.nota"
                                floatingLabel={true}
                                placeholder="---"
                                type="text"
                                name="nota"
                                value={formModel.nota}
                                required={false}
                                rows={5}
                                onChangeHandler={this.onChangeHandler}
                                onBlurHandler={this.onBlurHandler}
                                isInvalid={validationContainer.nota.invalid}
                                error={validationContainer.nota.message}
                                label="Testo*"
                            />
                        </Col>
                    </Row>
                    <Row className="pt-2">
                        <Col sm={12}>
                            <BaseInput
                                controlId="formNuovaNota.followers"
                                floatingLabel={true}
                                placeholder="---"
                                type="email"
                                name="followers"
                                value={formModel.followers}
                                required={false}
                                fullWidth={true}
                                onChangeHandler={this.onChangeHandler}
                                onBlurHandler={this.onBlurHandler}
                                isInvalid={validationContainer.followers.invalid}
                                error={validationContainer.followers.message}
                                label={`Followers${!notaInterna ? "*" : ""}`}
                            />
                        </Col>
                    </Row>
                    <Row className="align-items-center pt-3">
                        <Col>
                            <BaseCheckbox
                                controlId="formNuovaNota.notaInterna"
                                name="notaInterna"
                                label="Aggiungi come NOTA INTERNA"
                                value={formModel.notaInterna}
                                required={false}
                                onChangeHandler={this.onChangeHandler}
                                onBlurHandler={this.onBlurHandler}
                                isInvalid={validationContainer.notaInterna.invalid}
                                error={validationContainer.notaInterna.message}
                            />
                        </Col>
                    </Row>
                </Form>
                <Row className="align-items-center mt-4">
                    <Col sm={12}>
                        <span className="font-weight-bold font-italic">* campo obbligatorio</span>
                    </Col>
                </Row>
            </>
        );
    };

    private handleAnnullaModal = () => {
        this.props.onClose();
    };

    private handleConferma = async (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        this.onSubmitHandler(event);
    };

    public render(): JSX.Element | null {
        const modalBody = this.getModalBody();
        const { open, dettaglioPreventivo, notaDetails } = this.props;

        const idRichiestaPrev =
            dettaglioPreventivo && dettaglioPreventivo.idEstimateRequest !== ""
                ? dettaglioPreventivo.idEstimateRequest
                : notaDetails && notaDetails.idEstimateRequest !== 0
                ? notaDetails.idEstimateRequest
                : "0";

        return (
            <>
                {" "}
                {this.props.isRequesting ? (
                    <LoadingIndicator className="mb-3" isActive={true} />
                ) : (
                    <BaseModal
                        title={"Cronologia e Note - " + idRichiestaPrev}
                        show={open}
                        closeOnOutClick={false}
                        className="note"
                        centered={true}
                        bodyLeft={true}
                        scrollable={true}
                        size="xl"
                        body={<>{modalBody}</>}
                        footer={
                            <>
                                <BaseButton
                                    type="button"
                                    color="grey"
                                    label={"Chiudi"}
                                    icon={IconNameEnum.Ko}
                                    onClickHandler={this.handleAnnullaModal}
                                    className="mr-5"
                                />
                                <BaseButton
                                    type="submit"
                                    color="blue"
                                    label={"Conferma"}
                                    icon={IconNameEnum.Ok}
                                    onClickHandler={this.handleConferma}
                                />
                            </>
                        }
                    />
                )}
            </>
        );
    }
}

export { NotePopup as Unconnected };
export default connect(mapStateToProps)(NotePopup);
